import api from '.';
import {
  CareHomeShortlist,
  CareHomeShortlistData,
  Shortlist,
  ShortlistResult,
  ShortlistItem,
} from '@karehero/models';

export const careHomeShortlistApi = api.injectEndpoints({
  endpoints: (builder) => ({
    legacyMigrateShortlist: builder.mutation<
      void,
      { careProfileID: string; shortlistID: string }
    >({
      query: ({ careProfileID, shortlistID }) => ({
        url: `/care-profile/${careProfileID}/care-home-shortlist/${shortlistID}/migrate`,
        method: 'POST',
      }),
      invalidatesTags: [
        'CareHomeShortlist',
        'CareHomeShortlistData',
        'AdminCareHomeShortlist',
        'AdminCareHomeShortlistData',
        'Shortlists',
      ],
    }),
    legacyCreateShortlist: builder.mutation<
      CareHomeShortlist,
      CareHomeShortlist
    >({
      query: (careHomeShortlist) => ({
        url: '/care-home-shortlist',
        method: 'POST',
        body: careHomeShortlist,
      }),
      invalidatesTags: [
        'CareHomeShortlist',
        'CareHomeShortlistData',
        'AdminCareHomeShortlist',
        'AdminCareHomeShortlistData',
      ],
    }),
    legacyUpdateShortlist: builder.mutation<
      CareHomeShortlist,
      CareHomeShortlist & { id: string }
    >({
      query: (careHomeShortlist) => ({
        url: `/care-home-shortlist/${careHomeShortlist.id}`,
        method: 'PUT',
        body: careHomeShortlist,
      }),
      invalidatesTags: [
        'CareHomeShortlist',
        'CareHomeShortlistData',
        'AdminCareHomeShortlist',
        'AdminCareHomeShortlistData',
      ],
    }),
    getShortlistBySession: builder.query<any, void>({
      query: () => ({ url: `/care-profile/care-home-shortlist` }),
      providesTags: ['CareHomeShortlist'],
    }),
    getShortlistDataBySession: builder.query<any, void>({
      query: () => ({ url: `/care-profile/care-home-shortlist/data` }),
      providesTags: ['CareHomeShortlistData'],
    }),
    getShortlistByCareProfileId: builder.query<CareHomeShortlist, string>({
      query: (careProfileID) => ({
        url: `/care-profile/${careProfileID}/care-home-shortlist`,
      }),
      providesTags: ['AdminCareHomeShortlist'],
    }),
    getShortlistDataByCareProfileId: builder.query<
      CareHomeShortlistData,
      string
    >({
      query: (careProfileID) => ({
        url: `/care-profile/${careProfileID}/care-home-shortlist/data`,
      }),
      providesTags: ['AdminCareHomeShortlistData'],
    }),
    markShortlistCareHomeSecuredByCareProfileId: builder.mutation<
      any,
      CareHomeShortlist
    >({
      query: (data: CareHomeShortlist) => ({
        url: `/care-profile/${data.careProfileID}/care-home-secured`,
        method: 'POST',
        body: { providerID: data.securedRegulatoryID },
      }),
      invalidatesTags: ['AdminCareHomeShortlist'],
    }),
    markShortlistCareHomeSecured: builder.mutation<any, string>({
      query: (regulatoryID: string) => ({
        url: `/care-profile/care-home-secured`,
        method: 'POST',
        body: { regulatoryID },
      }),
      invalidatesTags: ['CareHomeShortlist'],
    }),
    listShortlists: builder.query<ShortlistResult[], string>({
      query: (careCircleID: string) => ({
        url: `/care-circle/${careCircleID}/shortlist`,
        method: 'GET',
      }),
      providesTags: ['Shortlists'],
    }),
    listPublishedShortlists: builder.query<ShortlistResult[], string>({
      query: (careCircleID: string) => ({
        url: `/care-circle/${careCircleID}/published-shortlist`,
        method: 'GET',
      }),
      providesTags: ['Shortlists'],
    }),
    getShortlist: builder.query<
      ShortlistResult,
      { careCircleID: string; id: string }
    >({
      query: ({ careCircleID, id }) => ({
        url: `/care-circle/${careCircleID}/shortlist/${id}`,
        method: 'GET',
      }),
      providesTags: ['Shortlists'],
    }),
    getPublishedShortlist: builder.query<
      ShortlistResult,
      { careCircleID: string; id: string }
    >({
      query: ({ careCircleID, id }) => ({
        url: `/care-circle/${careCircleID}/published-shortlist/${id}`,
        method: 'GET',
      }),
      providesTags: ['Shortlists'],
    }),
    createShortlist: builder.mutation<
      Shortlist,
      { careCircleID: string; shortlist: Shortlist }
    >({
      query: ({ careCircleID, shortlist }) => ({
        url: `/care-circle/${careCircleID}/shortlist`,
        method: 'POST',
        body: shortlist,
      }),
      invalidatesTags: ['Shortlists'],
    }),
    updateShortlist: builder.mutation<
      Shortlist,
      { careCircleID: string; id: string; params: Partial<Shortlist> }
    >({
      query: ({ careCircleID, id, params }) => ({
        url: `/care-circle/${careCircleID}/shortlist/${id}`,
        method: 'PATCH',
        body: params,
      }),
      invalidatesTags: ['Shortlists'],
    }),
    publishShortlist: builder.mutation<
      void,
      { careCircleID: string; id: string }
    >({
      query: ({ careCircleID, id }) => ({
        url: `/care-circle/${careCircleID}/shortlist/${id}/publish`,
        method: 'POST',
      }),
      invalidatesTags: ['Shortlists'],
    }),
    archiveShortlist: builder.mutation<
      void,
      { careCircleID: string; id: string }
    >({
      query: ({ careCircleID, id }) => ({
        url: `/care-circle/${careCircleID}/shortlist/${id}/archive`,
        method: 'POST',
      }),
      invalidatesTags: ['Shortlists'],
    }),
    getShortlistProviders: builder.query<
      ShortlistItem[],
      { careCircleID: string; shortlistID: string }
    >({
      query: ({ careCircleID, shortlistID }) => ({
        url: `/care-circle/${careCircleID}/shortlist/${shortlistID}/providers`,
        method: 'GET',
      }),
      providesTags: ['Shortlists'],
    }),
    getPublishedShortlistProviders: builder.query<
      ShortlistItem[],
      { careCircleID: string; shortlistID: string }
    >({
      query: ({ careCircleID, shortlistID }) => ({
        url: `/care-circle/${careCircleID}/published-shortlist/${shortlistID}/providers`,
        method: 'GET',
      }),
      providesTags: ['Shortlists'],
    }),
    addShortlistItem: builder.mutation<
      Shortlist,
      {
        careCircleID: string;
        shortlistID: string;
        regulatoryId: string;
        position?: number;
      }
    >({
      query: ({ careCircleID, shortlistID, regulatoryId, position = 0 }) => ({
        url: `/care-circle/${careCircleID}/shortlist/${shortlistID}/items`,
        method: 'POST',
        body: { regulatoryId, position },
      }),
      invalidatesTags: ['Shortlists'],
    }),
    removeShortlistItem: builder.mutation<
      Shortlist,
      { careCircleID: string; shortlistID: string; regulatoryID: string }
    >({
      query: ({ careCircleID, shortlistID, regulatoryID }) => ({
        url: `/care-circle/${careCircleID}/shortlist/${shortlistID}/items/${regulatoryID}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Shortlists'],
    }),
    updateShortlistItem: builder.mutation<
      Shortlist,
      {
        careCircleID: string;
        shortlistID: string;
        regulatoryID: string;
        params: {
          note?: string;
          position?: number;
          contacted?: boolean;
          secured?: boolean;
          unavailable?: boolean;
        };
      }
    >({
      query: ({ careCircleID, shortlistID, regulatoryID, params }) => ({
        url: `/care-circle/${careCircleID}/shortlist/${shortlistID}/items/${regulatoryID}`,
        method: 'PATCH',
        body: params,
      }),
      invalidatesTags: ['Shortlists'],
    }),
    dismissShortlistItem: builder.mutation<
      Shortlist,
      {
        careCircleID: string;
        shortlistID: string;
        regulatoryID: string;
        reason: string;
        detail?: string;
      }
    >({
      query: ({ careCircleID, shortlistID, regulatoryID, reason, detail }) => ({
        url: `/care-circle/${careCircleID}/shortlist/${shortlistID}/items/${regulatoryID}/dismiss`,
        method: 'POST',
        body: { reason, detail },
      }),
      invalidatesTags: ['Shortlists'],
    }),
    restoreShortlistItem: builder.mutation<
      Shortlist,
      { careCircleID: string; shortlistID: string; regulatoryID: string }
    >({
      query: ({ careCircleID, shortlistID, regulatoryID }) => ({
        url: `/care-circle/${careCircleID}/shortlist/${shortlistID}/items/${regulatoryID}/restore`,
        method: 'POST',
      }),
      invalidatesTags: ['Shortlists'],
    }),
    getShortlistAdminProviders: builder.query<
      ShortlistItem[],
      { careCircleID: string; shortlistID: string }
    >({
      query: ({ careCircleID, shortlistID }) => ({
        url: `/care-circle/${careCircleID}/shortlist/${shortlistID}/providers`,
        method: 'GET',
      }),
      providesTags: ['Shortlists'],
    }),
    updateShortlistItemStatus: builder.mutation<
      ShortlistItem,
      {
        careCircleID: string;
        shortlistID: string;
        regulatoryID: string;
        secured: boolean;
      }
    >({
      query: ({ careCircleID, shortlistID, regulatoryID, secured }) => ({
        url: `/care-circle/${careCircleID}/shortlist/${shortlistID}/items/${regulatoryID}/status`,
        method: 'POST',
        body: { secured },
      }),
      invalidatesTags: ['Shortlists'],
    }),
    updateShortlistStatus: builder.mutation<
      void,
      { careCircleID: string; shortlistID: string; securedElsewhere: boolean }
    >({
      query: ({ careCircleID, shortlistID, securedElsewhere }) => ({
        url: `/care-circle/${careCircleID}/shortlist/${shortlistID}/status`,
        method: 'POST',
        body: { securedElsewhere },
      }),
      invalidatesTags: ['Shortlists'],
    }),
  }),
});

export const {
  useLegacyCreateShortlistMutation,
  useLegacyUpdateShortlistMutation,
  useLegacyMigrateShortlistMutation,
  useCreateShortlistMutation,
  useUpdateShortlistMutation,
  useGetShortlistBySessionQuery,
  useGetPublishedShortlistQuery,
  useGetShortlistQuery,
  useListShortlistsQuery,
  useListPublishedShortlistsQuery,
  usePublishShortlistMutation,
  useArchiveShortlistMutation,
  useGetShortlistDataBySessionQuery,
  useGetShortlistByCareProfileIdQuery,
  useGetShortlistDataByCareProfileIdQuery,
  useMarkShortlistCareHomeSecuredMutation,
  useMarkShortlistCareHomeSecuredByCareProfileIdMutation,
  useGetPublishedShortlistProvidersQuery,
  useGetShortlistProvidersQuery,
  useUpdateShortlistItemStatusMutation,
  useUpdateShortlistStatusMutation,
  useAddShortlistItemMutation,
  useRemoveShortlistItemMutation,
  useUpdateShortlistItemMutation,
  useDismissShortlistItemMutation,
  useRestoreShortlistItemMutation,
  useGetShortlistAdminProvidersQuery,
} = careHomeShortlistApi;

export const invalidateShortlistTags = () =>
  careHomeShortlistApi.util.invalidateTags(['Shortlists']);

export default careHomeShortlistApi;
